import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Loader2, MapPin, Search, Filter, X, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const [trees, setTrees] = useState([]);
  const [filteredTrees, setFilteredTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [foundTrees, setFoundTrees] = useState([]);

  useEffect(() => {
    // Load found trees from localStorage
    const saved = localStorage.getItem('foundTrees');
    if (saved) {
      setFoundTrees(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    const fetchTrees = async () => {
      try {
        const response = await axios.get(`${API}/trees`);
        setTrees(response.data);
        setFilteredTrees(response.data);
      } catch (error) {
        console.error('Error fetching trees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrees();
  }, []);

  const toggleFoundTree = (treeId, treeName) => {
    let updated;
    if (foundTrees.includes(treeId)) {
      updated = foundTrees.filter(id => id !== treeId);
      toast.success(`${treeName} unmarked`);
    } else {
      updated = [...foundTrees, treeId];
      toast.success(`${treeName} found! 🌳`);
      
      // Check if all trees are found
      if (updated.length === trees.length && trees.length > 0) {
        setTimeout(() => {
          toast.success('🎉 Congratulations! You found all the trees!', {
            duration: 5000,
          });
        }, 500);
      }
    }
    setFoundTrees(updated);
    localStorage.setItem('foundTrees', JSON.stringify(updated));
  };

  useEffect(() => {
    let filtered = trees;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (tree) =>
          tree.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tree.scientific_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tree.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (tree.city && tree.city.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Family filter
    if (selectedFamily !== 'all') {
      filtered = filtered.filter((tree) => tree.family === selectedFamily);
    }

    // Conservation status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((tree) => tree.conservation_status === selectedStatus);
    }

    // City filter
    if (selectedCity !== 'all') {
      filtered = filtered.filter((tree) => tree.city === selectedCity);
    }

    setFilteredTrees(filtered);
  }, [searchQuery, selectedFamily, selectedStatus, selectedCity, trees]);

  // Get unique families, statuses, and cities
  const families = ['all', ...new Set(trees.map((tree) => tree.family))];
  const statuses = ['all', ...new Set(trees.map((tree) => tree.conservation_status))];
  const cities = ['all', ...new Set(trees.filter(tree => tree.city).map((tree) => tree.city))];

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedFamily('all');
    setSelectedStatus('all');
    setSelectedCity('all');
  };

  const hasActiveFilters = searchQuery || selectedFamily !== 'all' || selectedStatus !== 'all' || selectedCity !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="gallery-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-primary mb-2" data-testid="gallery-title">
                Tree Gallery
              </h1>
              <p className="text-base text-muted-foreground">
                Browse our collection of {trees.length} Australian trees
              </p>
            </div>
            
            {/* Progress Indicator */}
            <div className="bg-white border border-border rounded-lg px-6 py-4 shadow-sm">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">
                  {foundTrees.length}/{trees.length}
                </div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Trees Found
                </div>
                {foundTrees.length === trees.length && trees.length > 0 && (
                  <div className="text-xl mt-2">🎉</div>
                )}
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search trees by name, scientific name, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                data-testid="search-input"
              />
            </div>

            {/* Filter Toggle and Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors"
                data-testid="filter-toggle-button"
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters</span>
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                  data-testid="clear-filters-button"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm font-medium">Clear Filters</span>
                </button>
              )}

              <div className="text-sm text-muted-foreground">
                Showing {filteredTrees.length} of {trees.length} trees
              </div>
            </div>

            {/* Filter Dropdowns */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-border rounded-lg bg-white"
                data-testid="filter-panel"
              >
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    City/Town
                  </label>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="city-filter"
                  >
                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city === 'all' ? 'All Locations' : city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Family
                  </label>
                  <select
                    value={selectedFamily}
                    onChange={(e) => setSelectedFamily(e.target.value)}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="family-filter"
                  >
                    {families.map((family) => (
                      <option key={family} value={family}>
                        {family === 'all' ? 'All Families' : family}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Conservation Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="status-filter"
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status === 'all' ? 'All Statuses' : status}
                      </option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <div className="bento-grid" data-testid="tree-gallery">
          {filteredTrees.map((tree, index) => {
            const isFound = foundTrees.includes(tree.id);
            return (
              <motion.div
                key={tree.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="bento-item block group relative">
                  {/* Found Checkbox */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFoundTree(tree.id, tree.name);
                    }}
                    className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition-all shadow-lg"
                    title={isFound ? "Mark as not found" : "Mark as found"}
                  >
                    {isFound ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                  </button>

                  <Link
                    to={`/tree/${tree.id}`}
                    data-testid={`tree-card-${tree.id}`}
                  >
                    <div className="relative h-full min-h-[280px]">
                      <img
                        src={tree.image_url}
                        alt={tree.name}
                        className="tree-image"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="font-heading font-semibold text-lg mb-1">
                          {tree.name}
                        </h3>
                        <p className="text-sm italic opacity-90 mb-2">
                          {tree.scientific_name}
                        </p>
                        <div className="flex items-center gap-1 text-xs opacity-90">
                          <MapPin className="w-3 h-3" />
                          <span>{tree.habitat}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredTrees.length === 0 && (
          <div className="text-center py-12" data-testid="no-results-message">
            <p className="text-muted-foreground mb-4">
              No trees found matching your search criteria.
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;